import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import {
  ListViewDataResult,
  PagerSettings,
} from '@progress/kendo-angular-listview';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import {
  DISTINCT_ITEMS_SOURCE,
  DistinctItemsSource,
  GridState,
  GridStateCompositeFilterDescriptor,
} from '../../models';
import { SelectablePrimitive } from '../../mixins';
import { LoaderService, MessageService } from '../../services';

@Component({
  selector: 'app-custom-filter',
  templateUrl: './custom-filter.component.html',
  styleUrl: './custom-filter.component.scss',
})
export class CustomFilterComponent {
  private static readonly EAGER_TAKE = 100000;

  @Input() field!: string;
  @Input() type!: string;
  @Input() view!: string;
  @Input() productType!: string;
  @Input() preselected: string[] = [];
  @Input() restaurantId?: string;
  @Input() extraParams?: Record<string, string | string[]>;

  @Input() lazy: boolean = true;

  @Output() filterChanged: EventEmitter<string[]> = new EventEmitter<
    string[]
  >();

  @Input()
  filter?: GridStateCompositeFilterDescriptor =
    new GridStateCompositeFilterDescriptor();

  state!: GridState;

  tierId: string = '';
  allSelected: boolean = false;
  data!: ListViewDataResult;
  includes: string = '';
  itemSearch: Subject<string> = new Subject<string>();
  pagerSettings: PagerSettings = {
    buttonCount: 3,
    info: true,
    type: 'numeric',
    previousNext: true,
  };
  selectableItems: SelectablePrimitive<string>[] = [];
  masterSelected: any[] = [];
  pageSize: number = 20;
  loadedItems: SelectablePrimitive<string>[] = [];

  constructor(
    private loaderService: LoaderService,
    private messageService: MessageService,
    @Inject(DISTINCT_ITEMS_SOURCE) private service: DistinctItemsSource,
  ) {}

  ngOnInit() {
    this.prepopulatePreselected();
    this.subscribeToAutocomplete();
    this.buildDefaultState();
    this.fetchItems();
  }

  prepopulatePreselected() {
    this.masterSelected = [...this.preselected];
  }

  buildDefaultState() {
    this.state = new GridState(
      this.pageSize,
      [{ field: this.field, dir: 'asc' }],
      this.tierId,
      this.productType,
    );

    this.state.filter = new GridStateCompositeFilterDescriptor();

    // remove the current field, otherwise it will not allow to be changed
    if (this.filter) {
      const removedField = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(this.filter, this.field),
      );
      if (removedField) {
        this.state.filter = removedField;
      }
    }

    this.state.filter = this.state.filter.and({
      field: this.field,
      operator: 'isnotnull',
    });

    if (!this.lazy) {
      this.state.skip = 0;
      this.state.take = CustomFilterComponent.EAGER_TAKE;
    }
  }

  fetchItems(state?: GridState) {
    if (!state) state = this.state;
    this.loaderService.show();
    this.service
      .getDistinctItems(
        state,
        this.view,
        this.field,
        this.restaurantId,
        this.extraParams,
      )
      .subscribe({
        next: (listViewResult) => {
          this.data = listViewResult;
          this.selectableItems = this.data.data.map((item) => {
            const isSelected =
              this.allSelected ||
              this.masterSelected.includes(item[this.field]);
            return new SelectablePrimitive<string>(
              item[this.field],
              isSelected,
            );
          });

          if (!this.lazy) {
            this.loadedItems = this.selectableItems;
            this.applySearch();
          }

          this.updateSelected();
          this.syncAllSelected();

          this.loaderService.hide();
        },
        error: (err) => {
          this.messageService.error(err);
          this.loaderService.hide();
        },
      });
  }

  updateSelected() {
    this.selectableItems.forEach((item) => {
      if (item.selected) {
        if (!this.masterSelected.includes(item.value)) {
          this.masterSelected.push(item.value);
        }
      } else {
        this.masterSelected = this.masterSelected.filter(
          (selected) => selected !== item.value,
        );
      }
    });

    this.filterChanged.emit(this.masterSelected);
  }

  handleFilter(item: string) {
    this.itemSearch.next(item);
  }

  handleScroll() {
    if (!this.lazy || this.state.take! >= this.data.total) return;
    this.state.take! += this.pageSize;
    this.fetchItems(this.state);
  }

  subscribeToAutocomplete() {
    this.itemSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.includes = searchText;

          if (!this.lazy) {
            this.applySearch();
            return;
          }

          this.state.resetPager(this.pageSize);
          this.buildDefaultState();
          if (this.includes) {
            this.state.filter = this.state.filter?.and({
              field: this.field,
              ignoreCase: true,
              operator: this.type == 'string' ? 'contains' : 'eq',
              value: this.includes,
            });
          }

          this.fetchItems(this.state);
        }),
      )
      .subscribe();
  }
  
  applySearch() {
    const text = `${this.includes ?? ''}`.toLowerCase();

    this.selectableItems = this.loadedItems.filter((item) => {
      if (!text) return true;

      const value = `${item.value}`.toLowerCase();
      return this.type == 'string' ? value.includes(text) : value === text;
    });
  }

  toggleSelect(dataItem: SelectablePrimitive<string>) {
    dataItem.selected = !dataItem.selected;

    // Unchecking a single value breaks "Select All", otherwise the next fetch would check it again
    if (!dataItem.selected) this.allSelected = false;

    this.updateSelected();
  }

  toggleSelectAll() {
    this.allSelected = !this.allSelected;

    if (!this.lazy) {
      this.selectableItems.forEach((item) => (item.selected = this.allSelected));
      this.updateSelected();
      return;
    }


    // Unchecking "Select All" has to drop every value, not only stop forcing them to checked
    if (!this.allSelected) this.masterSelected = [];

    this.state.skip = 0;
    this.state.take = this.data.total;
    this.fetchItems(this.state);
  }

  // Shows "Select All" checked when every value of the list is already selected (e.g. reopening the filter)
  private syncAllSelected() {
    if (this.allSelected || this.includes) return;

    this.allSelected =
      this.data.total > 0 &&
      this.masterSelected.length >= this.data.total &&
      this.selectableItems.every((item) => item.selected);
  }
}
